import { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'motion/react';
import { RiLockLine, RiEyeLine, RiEyeOffLine, RiCheckLine, RiErrorWarningLine } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (!search.token) {
      setTokenError(true);
      toast.error('Invalid or missing reset token');
    }
  }, [search.token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!search.token) {
      toast.error('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/password-reset/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: search.token,
          newPassword: data.password,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.message || 'Failed to reset password');

        // If token is expired/invalid, show error state
        if (result.message?.includes('expired') || result.message?.includes('invalid')) {
          setTokenError(true);
        }
        return;
      }

      setIsSuccess(true);
      toast.success('Password reset successfully!');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate({ to: '/login' });
      }, 2000);
    } catch (error) {
      toast.error('An unexpected error occurred');
      console.error('Reset password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 8) return { strength: 25, label: 'Weak', color: 'bg-red-500' };

    let strength = 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 12.5;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5;

    if (strength <= 50) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 75) return { strength, label: 'Good', color: 'bg-yellow-500' };
    return { strength, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(form.watch('password') || '');

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center flex-1 p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src={logoSrc} alt="Moniva" className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">Moniva</h1>
              <p className="text-sm text-muted-foreground">Database Management</p>
            </div>
          </div>

          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-2xl">Reset Your Password</CardTitle>
              <CardDescription>
                {isSuccess
                  ? 'Your password has been reset successfully'
                  : tokenError
                    ? 'This reset link is invalid or has expired'
                    : 'Enter your new password below'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center p-4 rounded-full bg-green-500/10 w-fit mx-auto">
                    <RiCheckLine className="size-8 text-green-500" />
                  </div>
                  <Alert className="border-green-500/20 bg-green-500/5">
                    <RiCheckLine className="h-4 w-4 text-green-500" />
                    <AlertDescription>
                      Your password has been reset successfully. You can now sign in with your new password.
                    </AlertDescription>
                  </Alert>
                  <p className="text-sm text-center text-muted-foreground">
                    Redirecting to sign in page...
                  </p>
                </motion.div>
              ) : tokenError ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center p-4 rounded-full bg-destructive/10 w-fit mx-auto">
                    <RiErrorWarningLine className="size-8 text-destructive" />
                  </div>
                  <Alert variant="destructive">
                    <RiErrorWarningLine className="h-4 w-4" />
                    <AlertDescription>
                      This password reset link is invalid or has expired. Reset links are valid for 1 hour.
                    </AlertDescription>
                  </Alert>
                  <div className="space-y-3">
                    <Button
                      className="w-full"
                      onClick={() => navigate({ to: '/forgot-password' })}
                    >
                      Request New Reset Link
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => navigate({ to: '/login' })}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your new password"
                                autoComplete="new-password"
                                disabled={isLoading}
                                autoFocus
                                {...field}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showPassword ? (
                                  <RiEyeOffLine className="size-4" />
                                ) : (
                                  <RiEyeLine className="size-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          {field.value && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Password strength</span>
                                <span className={`font-medium ${
                                  passwordStrength.strength <= 50
                                    ? 'text-red-500'
                                    : passwordStrength.strength <= 75
                                      ? 'text-yellow-500'
                                      : 'text-green-500'
                                }`}>
                                  {passwordStrength.label}
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                                  style={{ width: `${passwordStrength.strength}%` }}
                                />
                              </div>
                            </div>
                          )}
                          <FormDescription>
                            Must be at least 8 characters long
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Confirm your new password"
                                autoComplete="new-password"
                                disabled={isLoading}
                                {...field}
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showConfirmPassword ? (
                                  <RiEyeOffLine className="size-4" />
                                ) : (
                                  <RiEyeLine className="size-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                      {isLoading ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 rounded-full border-primary-foreground border-t-transparent"
                        />
                      ) : (
                        <>
                          <RiLockLine className="size-4 mr-2" />
                          Reset Password
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>

          {!isSuccess && !tokenError && (
            <div className="mt-6 text-sm text-center text-muted-foreground">
              <p>
                Remember your password?{' '}
                <button
                  onClick={() => navigate({ to: '/login' })}
                  className="font-medium text-primary hover:underline"
                >
                  Sign in
                </button>
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Right Side - Illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        className="items-center justify-center flex-1 hidden p-12 lg:flex bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="max-w-lg space-y-6 text-center relative z-10">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            src="/nodata.svg"
            alt="Reset Password"
            className="w-full h-auto opacity-90"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="space-y-3"
          >
            <h3 className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Secure Password Reset
            </h3>
            <p className="text-base text-muted-foreground leading-relaxed">
              Choose a strong, unique password to keep your database management account secure.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
